<template>
	<div class="activity-container">
		<LoadingIcon v-if="isLoading" alignment="center" />
		<FallBack
			v-if="!showLog"
			message="You have no activities to view"
			:border="false"
			iconStyle="far"
			icon="list-ul"
		/>
		<ul v-else-if="!isLoading && !error" class="log">
			<li
				class="activity"
				v-for="(log, index) in activityLog"
				:key="index"
			>
				<div class="container" v-if="showActivity(log)">
					<AlertTypeIcon class="icon" :alertType="log.activityType" />
					<span
						v-if="
							log.activityType ===
							ACTIVITY_TYPES.AUTOMATIC_ALERT_EMAIL
						"
						class="description"
					>
						Automatic
						<TextButton
							@onClick="handleAlertClick(log.primaryEntityId)"
							>{{ log.primaryEntityTypeDesc }} Alert</TextButton
						>
						email sent by UWM
					</span>
					<span
						v-else-if="
							log.activityType ===
							ACTIVITY_TYPES.MANUAL_ALERT_EMAIL
						"
						class="description"
					>
						<TextButton
							@onClick="handleAlertClick(log.primaryEntityId)"
							>{{ log.primaryEntityTypeDesc }} Alert</TextButton
						>
						email sent by {{ log.userFirstName }}
						{{ log.userLastName }}
					</span>
					<span
						v-else-if="
							log.activityType === ACTIVITY_TYPES.LOAN_ALERT
						"
						class="description"
					>
						<TextButton
							@onClick="handleAlertClick(log.primaryEntityId)"
							>{{ log.primaryEntityTypeDesc }} Alert</TextButton
						>
						triggered for
						<TextButton
							@onClick="handleAddressClick(log.secondaryEntityId)"
							>{{ log.secondaryEntityValue }}</TextButton
						>
					</span>
					<span
						v-else-if="
							log.activityType === ACTIVITY_TYPES.LEAD_ALERT
						"
						class="description"
					>
						<TextButton
							@onClick="handleAlertClick(log.primaryEntityId)"
							>{{ log.primaryEntityTypeDesc }} Alert</TextButton
						>
						triggered
					</span>
					<span
						v-else-if="log.activityType === ACTIVITY_TYPES.LEAD"
						class="description"
					>
						Contact added by {{ log.userFirstName }}
						{{ log.userLastName }}
					</span>
					<span
						v-else-if="log.activityType === ACTIVITY_TYPES.LOAN"
						class="description"
					>
						<TextButton
							@onClick="handleAddressClick(log.primaryEntityId)"
							>{{ log.primaryEntityValue }}</TextButton
						>
						added by {{ log.userFirstName }} {{ log.userLastName }}
					</span>
					<span
						v-else-if="
							log.activityType === ACTIVITY_TYPES.ALERT_NOTE
						"
						class="description"
					>
						Note added to
						<TextButton
							@onClick="handleAlertClick(log.primaryEntityId)"
							>{{ log.secondaryEntityValue }}</TextButton
						>
						by {{ log.userFirstName }} {{ log.userLastName }}
					</span>
					<span
						v-else-if="
							log.activityType === ACTIVITY_TYPES.LEAD_NOTE
						"
						class="description"
					>
						Note added by {{ log.userFirstName }}
						{{ log.userLastName }}
					</span>
					<span
						v-else-if="
							log.activityType === ACTIVITY_TYPES.LOAN_NOTE
						"
						class="description"
					>
						Note added to
						<TextButton
							@onClick="handleAddressClick(log.primaryEntityId)"
							>{{ log.secondaryEntityValue }}</TextButton
						>
						by {{ log.userFirstName }} {{ log.userLastName }}
					</span>
					<span
						v-else-if="
							log.activityType === ACTIVITY_TYPES.DRIP_CAMPAIGN
						"
						class="description"
					>
						{{ log.primaryEntityValue }} email sent by UWM
					</span>
					<span
						v-else-if="
							log.activityType ===
							ACTIVITY_TYPES.BRAND_BUILDER_EMAIL
						"
						class="description"
					>
						{{ log.primaryEntityValue }} email sent by
						{{ log.secondaryEntityValue }}
					</span>
					<span
						v-else-if="
							log.activityType ===
							ACTIVITY_TYPES.BLINK_LOAN_IMPORT
						"
						class="description"
					>
						<TextButton
							@onClick="handleAddressClick(log.primaryEntityId)"
						>
							{{ log.primaryEntityValue }}
						</TextButton>
						imported from Blink+
					</span>
					<span
						v-else-if="
							log.activityType ===
							ACTIVITY_TYPES.BLINK_LOAN_EXPORT
						"
						class="description"
					>
						<TextButton
							@onClick="handleAddressClick(log.primaryEntityId)"
						>
							{{ log.primaryEntityValue }}
						</TextButton>
						exported to Blink+ by {{ log.userFirstName }}
						{{ log.userLastName }}
					</span>
					<span
						v-else-if="
							log.activityType ===
							ACTIVITY_TYPES.BLINK_LEAD_IMPORT
						"
						class="description"
					>
						Contact imported from Blink+
					</span>
					<span
						v-else-if="
							log.activityType ===
							ACTIVITY_TYPES.BLINK_LEAD_EXPORT
						"
						class="description"
					>
						Contact exported to Blink+ by {{ log.userFirstName }}
						{{ log.userLastName }}
					</span>
					<span
						v-else-if="
							log.activityType === ACTIVITY_TYPES.EASE_LEAD_IMPORT
						"
						class="description"
					>
						{{ log.primaryEntityValue }} imported from EASE
					</span>
					<span
						v-else-if="
							log.activityType === ACTIVITY_TYPES.EASE_LOAN_IMPORT
						"
						class="description"
					>
						<TextButton
							@onClick="handleAddressClick(log.primaryEntityId)"
						>
							{{ log.primaryEntityValue }}
						</TextButton>
						imported from EASE
					</span>
					<span class="date">
						{{
							new Date(log.createdDateTime).toLocaleDateString(
								'en-US',
								{
									month: 'long',
									day: 'numeric',
									year: 'numeric',
								},
							)
						}}
					</span>
					<span class="note" v-if="isNoteActivity(log.activityType)">
						{{ log.primaryEntityValue }}
					</span>
				</div>
			</li>
		</ul>
		<FallBack
			v-else-if="error"
			:message="error"
			:border="false"
			iconStyle="fal"
			icon="exclamation-triangle"
		/>
	</div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { isEmpty } from 'lodash';

import AlertTypeIcon from '../AlertTypeIcon.vue';
import FallBack from '../../../components/global/FallBack.vue';
import LoadingIcon from '../../../components/global/LoadingIcon.vue';
import TextButton from '../../../components/global/TextButton.vue';
import { ACTIVITY_TYPES } from '../constants';

export default {
	name: 'ActivityLog',
	props: {
		leadId: Number,
		contactName: String,
	},
	components: {
		AlertTypeIcon,
		FallBack,
		LoadingIcon,
		TextButton,
	},
	computed: {
		...mapState({
			isAE: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
		showLog() {
			if (!this.isLoading && !this.error) {
				const uwmActivities = this.activityLog.filter(
					(log) => log.uwmInd === true,
				);
				return !(this.isAE && isEmpty(uwmActivities));
			}
			return true;
		},
	},
	methods: {
		...mapActions(['getActivityLog']),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see note above
		isEmpty,
		showActivity(log) {
			let show = true;
			if (!Object.values(ACTIVITY_TYPES).includes(log.activityType)) {
				show = false;
			}
			if (this.isAE && log.uwmInd === false) {
				show = false;
			}
			return show;
		},
		isNoteActivity(activityType) {
			return (
				activityType === ACTIVITY_TYPES.ALERT_NOTE ||
				activityType === ACTIVITY_TYPES.LEAD_NOTE ||
				activityType === ACTIVITY_TYPES.LOAN_NOTE
			);
		},
		handleAlertClick(alertId) {
			this.$emit('onAlertClick', alertId);
		},
		handleAddressClick(loanId) {
			this.$emit('onAddressClick', loanId);
		},
	},
	async created() {
		this.isLoading = true;
		try {
			this.activityLog = await this.getActivityLog(this.leadId);
			this.error = false;
		} catch (error) {
			this.error =
				'Unable to retrieve activities. Please try again later.';
		} finally {
			this.isLoading = false;
		}
	},
	data() {
		return {
			activityLog: [],
			isLoading: false,
			error: false,
			ACTIVITY_TYPES,
		};
	},
};
</script>

<style lang="scss" scoped>
.activity-container {
	max-height: 600px;
	overflow-y: auto;
}

.log {
	list-style-type: none;
}

.activity {
	display: flex;
	flex-direction: column;
	position: relative;
	border-left: 1px solid $border-color-light;
	@include rem('padding-bottom', 30px);
	@include rem('padding-left', 36px);

	.container {
		display: flex;
		flex-direction: column;
	}

	.description {
		color: $black;
		font-weight: 500;
	}

	.icon {
		left: -24px;
		position: absolute;
		color: $black;
	}

	&:last-child {
		border-left: none;
		padding-bottom: 0;
	}
}

.date {
	@include rem('margin-bottom', 12px);
}

.note {
	width: 90%;
	background-color: $white;
	@include rem('padding', 15px);
	@include rem('border-radius', 6px);
	border: 1px solid $border-color-light;

	@media only screen and (max-width: 600px) {
		width: 85%;
	}
}
</style>
